import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';
import { MasterdataService } from '../../../../services/master-data/masterdata.service';
import { MarkdownComponent } from 'ngx-markdown';

export interface Announcement {
  id: number | string;
  release_version: number;
  release_note: string;
  is_active: boolean;
  status: string;
  attachment_name: string | null;
  attachment_url: string | null;
  created_by: string | null;
  created_on: string;
  modified_by: string | null;
  modified_on: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_read?: boolean;
}

@Component({
  selector: 'app-announcements',
  imports: [CommonModule, TooltipModule, MarkdownComponent],
  templateUrl: './announcements.component.html',
  styleUrl: './announcements.component.css',
})
export class AnnouncementsComponent implements OnInit {
  @HostListener('document:keydown.escape')
  isOpen = false;
  announcements: Announcement[] = [];
  isLoading = false;

  constructor(public masterdataService: MasterdataService) { }

  ngOnInit() {
    this.getAnnouncements();
  }

  toggle() {
    const opening = !this.isOpen;
    this.isOpen = opening;
    // Reading happens on open: record every unread item shown, so the
    // icon stops pulsing and stays stopped across refresh/navigation.
    if (opening) this.markVisibleAsRead();
  }

  close() {
    this.isOpen = false;
  }

  onEscape() {
    this.close();
  }

  getAnnouncements() {
    this.isLoading = true;
    this.masterdataService.getAnnouncementListIcon().subscribe({
      next: (res: any) => {
        // `current` answers `{message, data}` with `data` null when there is
        // nothing to announce; the fallbacks keep already-deployed flat
        // shapes working. The banner stays silent on empty -- no toast here.
        const raw = res?.data !== undefined ? res.data : (res?.results ?? res);
        const list = Array.isArray(raw) ? raw : raw?.release_version ? [raw] : [];
        this.announcements = list;
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('announcement fetch failed:', err);
        this.isLoading = false;
      },
    });
  }

  /** Pulse only for announcements that are active, in-window and unread. */
  get hasUnread(): boolean {
    return this.announcements.some((a) => this.isUnread(a));
  }

  get unreadLabel(): string {
    return this.hasUnread
      ? 'Announcements, new announcements available'
      : 'Announcements';
  }

  private isUnread(a: Announcement): boolean {
    if (!a?.is_active || a.is_read) return false;
    // In-window guard: a row can linger active past its end_date, but an
    // expired announcement must never pulse. Rows without a window
    // (legacy, pre-column) fall back to the active flag alone.
    if (!a.start_date || !a.end_date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (
      new Date(a.start_date + 'T00:00:00') <= today &&
      today <= new Date(a.end_date + 'T00:00:00')
    );
  }

  private markVisibleAsRead(): void {
    for (const a of this.announcements) {
      if (!this.isUnread(a)) continue;
      // Optimistic: stop the pulse at once; revert this row on failure.
      a.is_read = true;
      this.masterdataService.markAnnouncementRead(a.id).subscribe({
        next: () => { },
        error: () => {
          a.is_read = false;
        },
      });
    }
  }

  async downloadAttachment(a: Announcement) {
    if (!a.attachment_url) return;
    try {
      const res = await fetch(a.attachment_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.buildFileName(a.attachment_name ?? 'attachment');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('download failed:', err);
    }
  }

  private buildFileName(name: string): string {
    const base = (name ?? 'attachment').replace(/\.pdf$/i, '');
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${base}_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
      `_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.pdf`;
  }

  /** turns "<p>Hello&nbsp;&amp;&nbsp;bye</p><ul><li>one</li></ul>" into readable plain text */
  decodeHtml(html: string): string {
    const el = document.createElement('div');
    el.innerHTML = html ?? '';
    el.querySelectorAll('li').forEach((n) => n.prepend('• '));
    el.querySelectorAll('li, p, div, br').forEach((n) => n.append('\n'));
    return (el.textContent ?? '')
      .replaceAll('\u00a0', ' ')
      .replaceAll(/\n{2,}/g, '\n')
      .trim();
  }

}
